import type {
  Ack,
  CreateRoomPayload,
  RoomJoinResult,
  RoomState,
  SocketError,
} from '@point-ker/shared';
import { useCallback, useEffect, useState } from 'react';

import { useSocket, type AppClientSocket } from '@/socket/SocketProvider';
import { clearSession, loadSession, saveSession } from '@/socket/session';

/**
 * The room, as the UI sees it.
 *
 * All rendering reads `state`, which is whatever the server last broadcast.
 * Nothing is derived optimistically: a vote is not shown as cast until the
 * server says so. That costs a round trip of latency and buys the guarantee
 * that no two people ever see different rooms.
 */

type EmitWithAck = 'room:create' | 'room:join';

type AckPayload<E extends EmitWithAck> = E extends 'room:create'
  ? CreateRoomPayload
  : { roomId: string; name: string; asSpectator?: boolean };

/** Promise wrapper around socket.io acks, so callers can `await` and catch. */
function request<E extends EmitWithAck>(
  socket: AppClientSocket,
  event: E,
  payload: AckPayload<E>,
): Promise<RoomJoinResult> {
  return new Promise((resolve, reject) => {
    const ack: Ack<RoomJoinResult> = (response) => {
      if (response.ok) resolve(response.data);
      else reject(new Error(response.error.message));
    };
    // The overloads for acked emits do not narrow across a generic event name.
    (socket.emit as (event: string, payload: unknown, ack: unknown) => void)(event, payload, ack);
  });
}

export interface UseRoom {
  readonly state: RoomState | null;
  readonly participantId: string | null;
  readonly isHost: boolean;
  /**
   * The card you picked this round, or `null`.
   *
   * Tracked client-side out of necessity: `room:state` is one payload
   * broadcast to everyone, so it redacts card ids for *all* participants
   * before the reveal — including yours. Without this you could not see your
   * own selection. It claims nothing about anyone else, and it is cleared
   * whenever the server's state says you have no vote, so a reset or a new
   * story cannot leave a stale card highlighted. After reconnecting mid-round
   * it stays `null` until the reveal: the server confirms *that* you voted,
   * but no longer tells you what.
   */
  readonly myCardId: string | null;
  readonly error: SocketError | null;
  readonly dismissError: () => void;
  readonly createRoom: (payload: CreateRoomPayload) => Promise<RoomState>;
  readonly joinRoom: (roomId: string, name: string, asSpectator?: boolean) => Promise<RoomState>;
  readonly leaveRoom: () => void;
  readonly setStory: (title: string, description?: string, timerSeconds?: number) => void;
  readonly castVote: (cardId: string) => void;
  readonly revealVotes: () => void;
  readonly resetVotes: () => void;
  readonly commitEstimate: (estimate: string) => void;
  /** Host only: take a seat at the table, or step back to running the round. */
  readonly setSpectating: (isSpectator: boolean) => void;
}

export function useRoom(): UseRoom {
  const { socket } = useSocket();
  const [state, setState] = useState<RoomState | null>(null);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [error, setError] = useState<SocketError | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  const applyJoin = useCallback((result: RoomJoinResult) => {
    setState(result.state);
    setParticipantId(result.participantId);
    setIsHost(result.isHost);
    setSelectedCardId(null);
  }, []);

  /**
   * The server's roster of votes is the authority on whether you voted, so a
   * reset, a new story or a dropped vote clears the local highlight without
   * this hook having to know which event caused it.
   */
  const myCardId =
    state !== null && participantId !== null
      ? (state.votes.find((vote) => vote.participantId === participantId)?.cardId ??
        (state.votes.some((vote) => vote.participantId === participantId)
          ? selectedCardId
          : null))
      : null;

  useEffect(() => {
    const onState = (next: RoomState) => setState(next);
    const onError = (next: SocketError) => setError(next);

    /**
     * Rejoin after any reconnect — a network blip, or a dyno restart that
     * wiped every room. The server treats a returning name as the same
     * participant, so a blip restores the seat and the vote; if the room is
     * genuinely gone the stored session is dropped and the user lands back on
     * the join form rather than staring at a stale room.
     */
    const onConnect = () => {
      const session = loadSession();
      if (!session) return;
      request(socket, 'room:join', {
        roomId: session.roomId,
        name: session.name,
        asSpectator: session.isSpectator,
      })
        .then(applyJoin)
        .catch(() => {
          clearSession();
          setState(null);
          setParticipantId(null);
          setIsHost(false);
        });
    };

    socket.on('room:state', onState);
    socket.on('error', onError);
    socket.on('connect', onConnect);
    // Already connected when this mounted, so the event has been and gone.
    if (socket.connected) onConnect();

    return () => {
      socket.off('room:state', onState);
      socket.off('error', onError);
      socket.off('connect', onConnect);
    };
  }, [socket, applyJoin]);

  const createRoom = useCallback(
    async (payload: CreateRoomPayload) => {
      const result = await request(socket, 'room:create', payload);
      applyJoin(result);
      saveSession({
        roomId: result.state.id,
        name: payload.hostName,
        isSpectator: payload.hostAsSpectator === true,
      });
      return result.state;
    },
    [socket, applyJoin],
  );

  const joinRoom = useCallback(
    async (roomId: string, name: string, asSpectator = false) => {
      const result = await request(socket, 'room:join', { roomId, name, asSpectator });
      applyJoin(result);
      saveSession({ roomId: result.state.id, name, isSpectator: asSpectator });
      return result.state;
    },
    [socket, applyJoin],
  );

  const leaveRoom = useCallback(() => {
    socket.emit('room:leave');
    clearSession();
    setState(null);
    setParticipantId(null);
    setIsHost(false);
  }, [socket]);

  return {
    state,
    participantId,
    isHost,
    myCardId,
    error,
    dismissError: useCallback(() => setError(null), []),
    createRoom,
    joinRoom,
    leaveRoom,
    setStory: useCallback(
      (title: string, description?: string, timerSeconds?: number) =>
        socket.emit('story:set', { title, description, timerSeconds }),
      [socket],
    ),
    castVote: useCallback(
      (cardId: string) => {
        setSelectedCardId(cardId);
        socket.emit('vote:cast', { cardId });
      },
      [socket],
    ),
    revealVotes: useCallback(() => socket.emit('votes:reveal'), [socket]),
    resetVotes: useCallback(() => socket.emit('votes:reset'), [socket]),
    commitEstimate: useCallback(
      (estimate: string) => socket.emit('estimate:commit', { estimate }),
      [socket],
    ),
    setSpectating: useCallback(
      (isSpectator: boolean) => socket.emit('spectator:set', { isSpectator }),
      [socket],
    ),
  };
}
